/**
 * Science 3D Assets — superadmin + associate.
 *
 * Describe an asset in one sentence; the Scientific 3D Asset Factory builds it from trusted
 * scientific data (never text-to-3D), validates it, stores it in Firebase, and — once approved —
 * adds it to the 3D Asset Library. Progress streams live from Firestore.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  AlertTriangle, Atom, BookOpen, Box, Check, CheckCircle2, ChevronDown, Circle, Clock, ExternalLink,
  FlaskConical, Layers, Library, Loader2, Microscope, RotateCw, Settings2, ShieldCheck, Sparkles, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import { Textarea } from '../../Components/ui/textarea';
import { Input } from '../../Components/ui/input';
import { Progress } from '../../Components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/ui/tabs';
import Meshy3DViewer from '../../Components/Meshy3DViewer';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperadmin } from '../../utils/rbac';
import {
  SCI3D_STAGES, getSci3dModelUrls, resumeSci3dJob, reviewSci3dAsset, submitSci3dAsset,
  useSci3dAssets, useSci3dCandidates, useSci3dCatalog, useSci3dJob, useSci3dJobs,
  type Sci3dAsset, type Sci3dCatalog, type Sci3dJob, type Sci3dStatus,
} from '../../services/sci3dService';
import { AiBadge, AiSettingsDialog, FamilyBadge, RecognitionCard, SourcesPanel } from './Sci3dPanels';

const CURRICULA = ['CBSE', 'ICSE', 'IB', 'Cambridge', 'State Boards', 'Higher Education'];

const STATUS_STYLE: Record<Sci3dStatus, { label: string; className: string }> = {
  GENERATING: { label: 'Generating', className: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  APPROVED: { label: 'Approved', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  NEEDS_REVIEW: { label: 'Needs review', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  REJECTED: { label: 'Rejected', className: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  AWAITING_SOURCE: { label: 'Awaiting source', className: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
};

function StatusBadge({ status }: { status: Sci3dStatus }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.GENERATING;
  return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
}

function formatDuration(ms?: number | null) {
  if (!ms && ms !== 0) return '';
  return ms < 1000 ? `${ms} ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.floor(ms / 60000)} m ${Math.round((ms % 60000) / 1000)} s`;
}

function timeAgo(seconds?: number) {
  if (!seconds) return '';
  const diff = Date.now() / 1000 - seconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return new Date(seconds * 1000).toLocaleDateString();
}

function ScoreRing({ label, value }: { label: string; value?: number }) {
  const score = value ?? 0;
  const color = score >= 90 ? '#34d399' : score >= 70 ? '#fbbf24' : '#fb7185';
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-16 w-16">
        <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" className="text-muted/40" strokeWidth="3" />
          <motion.circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
            strokeDasharray="97.4" initial={{ strokeDashoffset: 97.4 }} animate={{ strokeDashoffset: 97.4 * (1 - score / 100) }}
            transition={{ duration: 0.9, ease: 'easeOut' }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-foreground">
          {value === undefined ? '–' : score}
        </span>
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------- Create

function CreatePanel({ catalog, onCreated }: { catalog: Sci3dCatalog | null; onCreated: (assetId: string) => void }) {
  const [prompt, setPrompt] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [grade, setGrade] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [chapter, setChapter] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!prompt.trim()) return;
    setSubmitting(true);
    try {
      const result = await submitSci3dAsset({
        prompt: prompt.trim(),
        ...(grade ? { grade: Number(grade) } : {}),
        ...(curriculum ? { curriculum } : {}),
        ...(chapter.trim() ? { chapter: chapter.trim() } : {}),
        ...(chapterId.trim() ? { chapterId: chapterId.trim() } : {}),
        ...(topicId.trim() ? { topicId: topicId.trim() } : {}),
      });
      toast.success(result.status === 'GENERATING' ? 'Generation started' : 'This asset already exists — showing it');
      onCreated(result.assetId);
      setPrompt('');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-border/60 bg-card/80 backdrop-blur">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg"><Sparkles className="h-5 w-5 text-primary" />Create a science asset</CardTitle>
        <CardDescription>Describe it in plain words. Built from trusted scientific data — never guessed by AI.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={500}
          placeholder="e.g. Water molecule (H2O) for grade 8 CBSE"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
          className="resize-none text-base" />
        {catalog && (
          <div className="space-y-2">
            {(catalog.families || []).map((f) => (
              <div key={f.family} className="flex flex-wrap items-center gap-1.5">
                <span className="w-40 shrink-0 text-[11px] text-muted-foreground">{f.label}{!f.autoApprovable && <span className="text-amber-400"> · review</span>}</span>
                {f.examples.map((example) => (
                  <button key={example} type="button" onClick={() => setPrompt(example)}
                    className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-muted-foreground transition hover:border-primary/60 hover:text-foreground">
                    {example}
                  </button>
                ))}
              </div>
            ))}
            {!catalog.families && catalog.topics.map((t) => (
              <button key={t.topic} type="button" onClick={() => setPrompt(t.examplePrompt)}
                className="mr-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground transition hover:border-primary/60 hover:text-foreground">
                {t.topic}
              </button>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setAdvanced(!advanced)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronDown className={`h-3.5 w-3.5 transition ${advanced ? 'rotate-180' : ''}`} />Advanced (grade, curriculum, lesson link)
        </button>
        <AnimatePresence initial={false}>
          {advanced && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="grid grid-cols-2 gap-3 overflow-hidden">
              <Input type="number" min={1} max={18} value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Grade (from prompt)" />
              <select value={curriculum} onChange={(e) => setCurriculum(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground">
                <option value="">Curriculum (from prompt / CBSE)</option>
                {CURRICULA.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="Chapter name" className="col-span-2" />
              <Input value={chapterId} onChange={(e) => setChapterId(e.target.value)} placeholder="Chapter ID (optional)" />
              <Input value={topicId} onChange={(e) => setTopicId(e.target.value)} placeholder="Topic ID (optional)" />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{catalog?.note}</p>
          <Button onClick={submit} disabled={submitting || !prompt.trim()} className="min-w-36">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-2 h-4 w-4" />}Generate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- Pipeline timeline

function substepText(substep: { step: string; n: number; of: number; view?: string } | undefined) {
  if (!substep) return '';
  if (substep.step === 'build') return 'Geometry built';
  if (substep.step === 'ai_generate') return `AI generation ${substep.n}/${substep.of} (Meshy)`;
  if (substep.step === 'lod') return `Level of detail ${substep.n}/${substep.of}`;
  return `Preview render ${substep.n}/${substep.of}${substep.view ? ` · ${substep.view}` : ''}`;
}

function PipelineTimeline({ job, catalog }: { job: Sci3dJob; catalog: Sci3dCatalog | null }) {
  const [resuming, setResuming] = useState(false);
  const failed = job.lastError && !job.runningStage && !job.halted;
  const executionUrl = job.n8nExecutionId && catalog?.n8nExecutionUrlTemplate
    ? catalog.n8nExecutionUrlTemplate.replace('{id}', job.n8nExecutionId) : null;

  const resume = async () => {
    setResuming(true);
    try { await resumeSci3dJob(job.assetId); toast.info('Resuming from the failed stage'); }
    catch (error: any) { toast.error(error.message); }
    finally { setResuming(false); }
  };

  return (
    <Card className="border-border/60 bg-card/80">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{job.request?.topic}</CardTitle>
            <CardDescription>
              Grade {job.request?.grade} · {job.request?.curriculum}{job.prompt ? ` · “${job.prompt}”` : ''}
            </CardDescription>
          </div>
          <StatusBadge status={job.status} />
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-muted-foreground">
          <span className="font-mono">{job.assetId}</span>
          {executionUrl && (
            <a href={executionUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
              n8n execution #{job.n8nExecutionId}<ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <RecognitionCard resolution={job.resolution} family={job.spec?.family} />
        {job.aiGenerated && (
          <div className="mb-3 flex items-center gap-2 text-xs text-orange-200"><AiBadge />No trusted source exists: this model will be AI-generated and must be reviewed.</div>
        )}
        <ol className="relative space-y-1">
          {SCI3D_STAGES.map((stage, index) => {
            const p = job.progress?.[stage.key];
            const state = p?.state || (job.completedStages?.includes(stage.key) ? 'done' : 'pending');
            const icon = state === 'done' ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              : state === 'running' ? <Loader2 className="h-5 w-5 animate-spin text-sky-400" />
              : state === 'failed' ? <XCircle className="h-5 w-5 text-rose-400" />
              : state === 'skipped' ? <Circle className="h-5 w-5 text-muted-foreground/40" />
              : <Circle className="h-5 w-5 text-muted-foreground/60" />;
            const sub = stage.key === 'generate' && state === 'running' ? p?.substep : undefined;
            return (
              <motion.li key={stage.key} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.04 }}
                className={`relative flex gap-3 rounded-lg px-2 py-2 ${state === 'running' ? 'bg-sky-500/5' : ''}`}>
                <div className="flex flex-col items-center">
                  {icon}
                  {index < SCI3D_STAGES.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-medium ${state === 'pending' || state === 'skipped' ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {index + 1}. {stage.label}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      {p?.durationMs != null && <><Clock className="h-3 w-3" />{formatDuration(p.durationMs)}</>}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{p?.detail || (state === 'failed' ? `Failed (${p?.error})` : stage.description)}</p>
                  {sub && (
                    <div className="mt-2 space-y-1">
                      <Progress value={(sub.n / sub.of) * 100} className="h-1.5" />
                      <p className="text-[11px] text-sky-300">{substepText(sub)}</p>
                    </div>
                  )}
                </div>
              </motion.li>
            );
          })}
        </ol>
        {job.haltReason && (
          <div className="mt-3 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />{job.haltReason}
          </div>
        )}
        {failed && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
            <span>Stopped at <b>{job.lastError!.stage}</b> ({job.lastError!.type}). Finished stages are kept.</span>
            <Button size="sm" variant="outline" onClick={resume} disabled={resuming}>
              {resuming ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCw className="mr-1 h-3 w-3" />}Resume
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- Asset detail

function AssetDetail({ asset, canReview }: { asset: Sci3dAsset; canReview: boolean }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [lod, setLod] = useState<'model.glb' | 'model_lod1.glb' | 'model_lod2.glb'>('model.glb');
  const [selectedPart, setSelectedPart] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrls({});
    getSci3dModelUrls(asset.assetId).then((u) => { if (!cancelled) setUrls(u); }).catch((e) => toast.error(e.message));
    return () => { cancelled = true; };
  }, [asset.assetId]);

  const part = asset.meshParts?.find((p) => p.name === selectedPart);

  return (
    <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
      <Card className="overflow-hidden border-border/60 bg-card/80">
        <div className="relative h-[420px] bg-gradient-to-b from-slate-900 to-slate-950">
          {urls[lod] ? (
            <Meshy3DViewer key={urls[lod]} modelUrl={urls[lod]} autoRotate showControls lighting="studio" className="h-full w-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          )}
          <div className="absolute left-3 top-3 flex gap-1 rounded-lg bg-black/40 p-1 backdrop-blur">
            {(['model.glb', 'model_lod1.glb', 'model_lod2.glb'] as const).map((name, i) => (
              <button key={name} onClick={() => setLod(name)}
                className={`rounded-md px-2 py-1 text-xs ${lod === name ? 'bg-primary text-primary-foreground' : 'text-slate-300 hover:text-white'}`}>
                LOD{i}{asset.lods?.[i] ? ` · ${asset.lods[i].triangles.toLocaleString()}` : ''}
              </button>
            ))}
          </div>
        </div>
        {asset.previews && (
          <div className="grid grid-cols-6 gap-1 border-t border-border/60 p-2">
            {Object.entries(asset.previews).map(([view, url]) => url && (
              <button key={view} onClick={() => setPreview(url)} className="group relative overflow-hidden rounded-md">
                <img src={url} alt={view} className="aspect-[4/3] w-full object-cover transition group-hover:scale-105" />
                <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-center text-[10px] capitalize text-white">{view}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="space-y-4">
        <Card className="border-border/60 bg-card/80">
          <CardContent className="flex items-center justify-around pt-6">
            <ScoreRing label="Scientific accuracy" value={asset.validation?.scientificAccuracyScore} />
            <ScoreRing label="Geometry quality" value={asset.validation?.geometryQualityScore} />
            <div className="flex flex-col items-center gap-2">
              <StatusBadge status={asset.status} />
              <FamilyBadge family={asset.family} />
              {asset.aiGenerated && <AiBadge />}
              {asset.libraryUrl && (
                <a href="/admin/3d-assets" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                  <Library className="h-3 w-3" />In 3D Asset Library
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {(asset.scientificNote || asset.wikidata) && (
          <Card className={asset.aiGenerated ? 'border-orange-500/30 bg-orange-500/5' : 'border-border/60 bg-card/80'}>
            <CardContent className="space-y-1 pt-4 text-xs text-muted-foreground">
              {asset.scientificNote && <p className={asset.aiGenerated ? 'text-orange-200' : ''}>{asset.scientificNote}</p>}
              {asset.wikidata && (
                <a href={asset.wikidata.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  Wikidata {asset.wikidata.qid}{asset.wikidata.description ? ` · ${asset.wikidata.description}` : ''}<ExternalLink className="h-3 w-3" />
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {asset.validation?.errors?.length > 0 && (
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="space-y-1 pt-4 text-xs text-amber-200">
              <p className="flex items-center gap-1 font-medium"><AlertTriangle className="h-4 w-4" />Why it needs review</p>
              {asset.missingRequiredStructures?.length ? (
                <p>Missing required structures: {asset.missingRequiredStructures.map((s) => s.replace(/_/g, ' ')).join(', ')}</p>
              ) : asset.validation.errors.slice(0, 4).map((e) => <p key={e}>{e}</p>)}
            </CardContent>
          </Card>
        )}

        <Card className="border-border/60 bg-card/80">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><Layers className="h-4 w-4" />Interactive parts ({asset.meshParts?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
              {asset.meshParts?.map((p) => (
                <button key={p.name} onClick={() => setSelectedPart(p.name === selectedPart ? null : p.name)}
                  className={`rounded-md border px-2 py-0.5 text-xs capitalize ${p.name === selectedPart ? 'border-primary bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  {p.label}
                </button>
              ))}
            </div>
            {part && (
              <div className="mt-3 grid grid-cols-2 gap-1 rounded-md bg-muted/30 p-2 text-xs text-muted-foreground">
                <span>Mesh</span><span className="font-mono text-foreground">{part.name}</span>
                {part.ontologyId && <><span>Ontology</span><span className="font-mono text-foreground">{part.ontologyId}</span></>}
                {part.element && <><span>Element</span><span className="text-foreground">{part.element}</span></>}
                {part.meanRadiusKm && <><span>Mean radius</span><span className="text-foreground">{part.meanRadiusKm.toLocaleString()} km</span></>}
                {part.semiMajorAxisAU && <><span>Orbit (a)</span><span className="text-foreground">{part.semiMajorAxisAU} AU</span></>}
                <span>Triangles</span><span className="text-foreground">{part.triangles.toLocaleString()}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/80">
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><BookOpen className="h-4 w-4" />Sources & licence</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs">
            {asset.sources?.map((s) => (
              <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="block rounded-md border border-border/60 p-2 hover:border-primary/50">
                <span className="font-medium text-foreground">{s.publisher}</span>
                <span className="block text-muted-foreground">{s.dataset} · {s.license}</span>
              </a>
            ))}
          </CardContent>
        </Card>

        {canReview && asset.status === 'NEEDS_REVIEW' && <ReviewActions asset={asset} />}
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPreview(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6">
            <img src={preview} alt="preview" className="max-h-full max-w-full rounded-lg shadow-2xl" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReviewActions({ asset }: { asset: Sci3dAsset }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const blocked = asset.validation?.errors?.length > 0;
  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(decision);
    try {
      await reviewSci3dAsset(asset.assetId, decision, asset.modelSha256, notes);
      toast.success(decision === 'approve' ? 'Approved and added to the 3D Asset Library' : 'Rejected');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4" />Superadmin review</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} placeholder="Notes (optional)" />
        {blocked && <p className="text-xs text-amber-300">Blocking scientific issues can't be waived — approve is disabled until a corrected version is generated.</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={() => decide('approve')} disabled={blocked || !!busy}>
            {busy === 'approve' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}Approve
          </Button>
          <Button size="sm" variant="destructive" onClick={() => decide('reject')} disabled={!!busy}>
            {busy === 'reject' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <XCircle className="mr-1 h-3 w-3" />}Reject
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Decision is bound to model hash {asset.modelSha256?.slice(0, 12)}…</p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------- Asset grid

function AssetGrid({ assets, onOpen, empty }: { assets: Sci3dAsset[]; onOpen: (a: Sci3dAsset) => void; empty: string }) {
  if (!assets.length) {
    return <div className="py-16 text-center text-muted-foreground"><Box className="mx-auto mb-3 h-10 w-10" />{empty}</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
      {assets.map((a, i) => (
        <motion.button key={a.assetId} onClick={() => onOpen(a)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i, 12) * 0.03 }}
          className="group overflow-hidden rounded-xl border border-border/60 bg-card/80 text-left transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg">
          <div className="aspect-[4/3] overflow-hidden bg-slate-900">
            {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
              : <div className="flex h-full items-center justify-center"><Atom className="h-8 w-8 text-muted-foreground" /></div>}
          </div>
          <div className="space-y-1 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium text-foreground">{a.title}</span>
              <StatusBadge status={a.status} />
            </div>
            <div className="flex flex-wrap gap-1"><FamilyBadge family={a.family} />{a.aiGenerated && <AiBadge />}</div>
            <p className="text-xs text-muted-foreground">{a.domain} · Grade {a.grade} · {a.curriculum}</p>
            <p className="text-xs text-muted-foreground">
              Sci {a.validation?.scientificAccuracyScore} · Geo {a.validation?.geometryQualityScore} · {timeAgo(a.registeredAt)}
            </p>
          </div>
        </motion.button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Page

export default function ScientificAssetFactory() {
  const { profile } = useAuth();
  const canReview = isSuperadmin(profile as any);
  const catalog = useSci3dCatalog();
  const jobs = useSci3dJobs(25);
  const assets = useSci3dAssets(100);
  const candidates = useSci3dCandidates();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState('create');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [openAsset, setOpenAsset] = useState<Sci3dAsset | null>(null);
  const activeJob = useSci3dJob(activeJobId);

  useEffect(() => {
    if (!activeJobId && jobs.items.length) setActiveJobId(jobs.items[0].assetId);
  }, [activeJobId, jobs.items]);

  const assetById = useMemo(() => new Map(assets.items.map((a) => [a.assetId, a])), [assets.items]);
  const reviewQueue = assets.items.filter((a) => a.status === 'NEEDS_REVIEW');
  const counts = {
    approved: assets.items.filter((a) => a.status === 'APPROVED').length,
    review: reviewQueue.length,
    running: jobs.items.filter((j) => j.status === 'GENERATING' && !j.lastError).length,
  };
  const activeAsset = activeJob && assetById.get(activeJob.assetId);
  const liveOpenAsset = openAsset ? assetById.get(openAsset.assetId) || openAsset : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight text-foreground">
              <span className="rounded-xl bg-gradient-to-br from-sky-500 to-violet-600 p-2"><Atom className="h-6 w-6 text-white" /></span>
              Science 3D Assets
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">Scientifically accurate, validated 3D models from trusted data — stored in Firebase, ready for lessons.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canReview && (
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}><Settings2 className="mr-1.5 h-4 w-4" />AI settings</Button>
            )}
            <Badge variant="outline" className={STATUS_STYLE.APPROVED.className}>{counts.approved} approved</Badge>
            <Badge variant="outline" className={STATUS_STYLE.NEEDS_REVIEW.className}>{counts.review} to review</Badge>
            <Badge variant="outline" className={STATUS_STYLE.GENERATING.className}>{counts.running} running</Badge>
          </div>
        </div>

        {(jobs.error || assets.error) && (
          <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
            Could not load data: {jobs.error || assets.error}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-5">
            <TabsTrigger value="create"><Sparkles className="mr-1.5 h-4 w-4" />Create</TabsTrigger>
            <TabsTrigger value="library"><Box className="mr-1.5 h-4 w-4" />Assets ({assets.items.length})</TabsTrigger>
            <TabsTrigger value="review"><ShieldCheck className="mr-1.5 h-4 w-4" />Review queue{counts.review ? ` (${counts.review})` : ''}</TabsTrigger>
            <TabsTrigger value="sources"><Microscope className="mr-1.5 h-4 w-4" />Sources{candidates.items.length ? ` (${candidates.items.length})` : ''}</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
              <div className="space-y-4">
                <CreatePanel catalog={catalog} onCreated={setActiveJobId} />
                <Card className="border-border/60 bg-card/80">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Recent runs</CardTitle></CardHeader>
                  <CardContent className="max-h-80 space-y-1 overflow-y-auto">
                    {jobs.loading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />}
                    {!jobs.loading && !jobs.items.length && <p className="text-sm text-muted-foreground">No runs yet — describe your first asset above.</p>}
                    {jobs.items.map((j) => {
                      const done = j.completedStages?.length || 0;
                      return (
                        <button key={j.assetId} onClick={() => setActiveJobId(j.assetId)}
                          className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition ${j.assetId === activeJobId ? 'bg-primary/10' : 'hover:bg-muted/40'}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-foreground">{j.request?.topic}</p>
                            <p className="text-xs text-muted-foreground">Grade {j.request?.grade} · {timeAgo(j.createdAt)} · {done}/7 stages</p>
                          </div>
                          {j.lastError && !j.runningStage && !j.halted ? <Badge variant="outline" className={STATUS_STYLE.REJECTED.className}>Stopped</Badge> : <StatusBadge status={j.status} />}
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
              <div>
                {activeJob ? <PipelineTimeline job={activeJob} catalog={catalog} /> : (
                  <Card className="flex h-full min-h-64 items-center justify-center border-dashed border-border/60 bg-card/40 text-sm text-muted-foreground">
                    Live progress appears here
                  </Card>
                )}
              </div>
            </div>
            {activeAsset && <AssetDetail asset={activeAsset} canReview={canReview} />}
          </TabsContent>

          <TabsContent value="library">
            {liveOpenAsset ? (
              <div className="space-y-3">
                <Button variant="outline" size="sm" onClick={() => setOpenAsset(null)}>← All assets</Button>
                <h2 className="text-xl font-semibold text-foreground">{liveOpenAsset.title}</h2>
                <AssetDetail asset={liveOpenAsset} canReview={canReview} />
              </div>
            ) : (
              <AssetGrid assets={assets.items} onOpen={setOpenAsset} empty={assets.loading ? 'Loading…' : 'No science assets yet'} />
            )}
          </TabsContent>

          <TabsContent value="sources">
            <p className="mb-4 text-xs text-muted-foreground">
              Topics without a scientific database are matched to CC0 museum scans. Approve the best scan once — every later request for that topic reuses it.
            </p>
            <SourcesPanel sets={candidates.items} loading={candidates.loading} />
          </TabsContent>

          <TabsContent value="review">
            {!canReview && <p className="mb-3 text-xs text-muted-foreground">Only superadmins can approve or reject. You can inspect items here.</p>}
            {liveOpenAsset && liveOpenAsset.status === 'NEEDS_REVIEW' ? (
              <div className="space-y-3">
                <Button variant="outline" size="sm" onClick={() => setOpenAsset(null)}>← Review queue</Button>
                <AssetDetail asset={liveOpenAsset} canReview={canReview} />
              </div>
            ) : (
              <AssetGrid assets={reviewQueue} onOpen={setOpenAsset} empty="Nothing waiting for review" />
            )}
          </TabsContent>
        </Tabs>
      </div>
      {canReview && <AiSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </div>
  );
}
